import { APP_TYPE, AppType } from "../app.type";
import { CustomersServiceType } from "./customers.service.type";
import {
  getCustomerSettings,
  getCustomerSettingsWithoutUser
} from "../api.service";
import { Inject, Injectable } from "@angular/core";
import { Settings } from "../../../../shared/components";
import { touch } from "../utils/app.utils";
import { Customer } from "../../../../shared/types/customers";
import { CustomerName } from "../../../../shared/config/customers";
import { PORTAL } from "../../../../shared/constants/portal";

export const TRANSPORT_CLIENT = "transport-client";
export const NON_TRANSPORT_CLIENT = "non-transport-client";

export const RM_CLIENT = "rm-client";
export const NON_RM_CLIENT = "non-rm-client";
export const OM_CLIENT = "om-client";
export const NON_OM_CLIENT = "non-om-client";

export const SPECIAL_CONFIGURATION = "special-configuration";
export const USUAL_CONFIGURATION = "usual-configuration";

@Injectable()
export class CustomersService implements CustomersServiceType {
  dataProvider = "";

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  get customers() {
    return [...this.app.state.customers].sort();
  }

  get current() {
    return this.app.state.customer;
  }

  get expectCurrent() {
    return this.app.expect(this.current);
  }

  getText(customer = this.expectCurrent) {
    if (customer === PORTAL) {
      return "Bahnportal";
    }
    if (customer === Customer.COOP) {
      return "COOP-M Portal";
    }
    if (customer === Customer.ATLAS) {
      return "ATLAS Elektronik";
    }
    if (customer === Customer.MTU) {
      return "RollsRoyce Solutions";
    }
    if (customer === Customer.XLIVE) {
      return "X-LiveConnect";
    }
    if (customer === Customer.SEW) {
      return "SEW Eurodrive";
    }
    if (customer === Customer.PLASTICOMNIUM) {
      return "Plastic Omnium";
    }
    if (customer === Customer.RHEINMETALLAIRDEFENCE) {
      return "RheinmetallAirDefence";
    }
    if (customer === Customer.RHEINMETALLITALY) {
      return "Rheinmetall Italia";
    }
    if (customer === Customer.RME) {
      return "Rheinmetall Electronics";
    }
    if (customer === Customer.RMMVO) {
      return "RMMVÖ";
    }
    if (customer === Customer.AMLAS) {
      return "AMLaS";
    }
    if (customer === Customer.OC) {
      return "Obsolescence Community";
    }
    if (customer === Customer.VIASTORE) {
      return "viastore SYSTEMS GmbH";
    }
    if (customer === Customer.TAL) {
      return "Toyota Automated Logistics";
    }
    if (customer === Customer.ONESUBSEA) {
      return "SLB OneSubsea";
    }
    if (customer === Customer.DIEHL) {
      return "Diehl Defence";
    }
    return customer.toUpperCase();
  }

  /**  Used for customers list filtration */
  getLowerCaseText(customer: string) {
    if (customer === "Bahnportal") {
      return PORTAL;
    }
    if (customer === "COOP-M Portal") {
      return Customer.COOP;
    }
    if (customer === "ATLAS Elektronik") {
      return Customer.ATLAS;
    }
    if (customer === "RollsRoyce Solutions") {
      return Customer.MTU;
    }
    if (customer === "X-LiveConnect") {
      return Customer.XLIVE;
    }
    if (customer === "SEW Eurodrive") {
      return Customer.SEW;
    }
    if (customer === "SLB OneSubsea") {
      return Customer.ONESUBSEA;
    }
    if (customer === "Diehl Defence") {
      return Customer.DIEHL;
    }
    if (customer === "Plastic Omnium") {
      return Customer.PLASTICOMNIUM;
    }
    if (customer === "Obsolescence Community") {
      return Customer.OC;
    }
    if (customer === "RMMVÖ") {
      return Customer.RMMVO;
    }
    if (customer === "viastore SYSTEMS GmbH") {
      return Customer.VIASTORE;
    }
    if (customer === "Rheinmetall Italia") {
      return Customer.RHEINMETALLITALY;
    }
    if (customer === "Rheinmetall Electronics") {
      return Customer.RME;
    }
    if (customer === "Toyota Automated Logistics") {
      return Customer.TAL;
    }
    return customer.toLowerCase();
  }

  getSyncCustomers(currentCustomer = this.current) {
    if (currentCustomer === PORTAL) {
      return this.customers.filter(
        (customer) =>
          customer !== PORTAL &&
          this.getCustomerType(customer) === TRANSPORT_CLIENT
      );
    } else {
      return [PORTAL];
    }
  }

  getCustomerType(customer: string = this.app.customers.expectCurrent) {
    const nonTransportClients = [
      Customer.KOMAX,
      Customer.SCHIEBEL,
      Customer.CAE,
      Customer.CEOTRONICS,
      Customer.AIRBUSDS,
      Customer.MAQUETGMBH,
      Customer.IAV,
      Customer.HERRMANNULTRASCHALL,
      Customer.LRE,
      Customer.LUTRON,
      Customer.KVB,
      Customer.RUAGCH,
      Customer.EMZ,
      Customer.ROSEN,
      Customer.TAE,
      Customer.TRIAL1,
      Customer.TRIAL2,
      Customer.TRIAL3,
      Customer.TRIAL4,
      Customer.TTI,
      Customer.MTU,
      Customer.ATLAS,
      Customer.DEMO,
      Customer.DEMOZ2DATA,
      Customer.KARLSTORZ,
      Customer.XLIVE,
      Customer.STADLER,
      Customer.AUTOFLUG,
      Customer.SEW,
      Customer.PLASTICOMNIUM,
      Customer.RHEINMETALL,
      Customer.RHEINMETALLITALY,
      Customer.RHEINMETALLAIRDEFENCE,
      Customer.RME,
      Customer.RMMVO,
      Customer.RWM,
      Customer.BELIMO,
      Customer.PILZ,
      Customer.RADWELL,
      Customer.KNDS,
      Customer.OC,
      Customer.HAGENUK,
      Customer.AMLAS,
      Customer.VANDERLANDE,
      Customer.THALES,
      Customer.EBERSPACHER,
      Customer.LEONARDO,
      Customer.PLATH,
      Customer.ONESUBSEA,
      Customer.DIEHL
    ];

    if (nonTransportClients.includes(customer)) {
      return NON_TRANSPORT_CLIENT;
    }

    return TRANSPORT_CLIENT;
  }

  getRMCustomers(): string {
    const rmClients = [
      Customer.KOMAX,
      Customer.SCHIEBEL,
      Customer.CEOTRONICS,
      Customer.AIRBUSDS,
      Customer.MAQUETGMBH,
      Customer.IAV,
      Customer.HERRMANNULTRASCHALL,
      Customer.LRE,
      Customer.LUTRON,
      Customer.KVB,
      Customer.RUAGCH,
      Customer.EMZ,
      Customer.ROSEN,
      Customer.TAE,
      Customer.TRIAL1,
      Customer.TRIAL2,
      Customer.TRIAL3,
      Customer.TRIAL4,
      Customer.TTI,
      Customer.MTU,
      Customer.NS,
      Customer.MRCE,
      Customer.DEMO,
      Customer.DEMOZ2DATA,
      Customer.ATLAS,
      Customer.KNDS,
      Customer.KARLSTORZ,
      Customer.XLIVE,
      Customer.STADLER,
      Customer.AUTOFLUG,
      Customer.SEW,
      Customer.PLASTICOMNIUM,
      Customer.RHEINMETALL,
      Customer.RHEINMETALLITALY,
      Customer.RHEINMETALLAIRDEFENCE,
      Customer.RME,
      Customer.RMMVO,
      Customer.RWM,
      Customer.CAE,
      Customer.BELIMO,
      Customer.PILZ,
      Customer.RADWELL,
      Customer.HAGENUK,
      Customer.AMLAS,
      Customer.VIASTORE,
      Customer.VANDERLANDE,
      Customer.THALES,
      Customer.EBERSPACHER,
      Customer.LEONARDO,
      Customer.PLATH,
      Customer.TAL,
      Customer.ONESUBSEA,
      Customer.DIEHL
    ];

    if (rmClients.includes(this.app.customers.expectCurrent)) {
      return RM_CLIENT;
    }

    return NON_RM_CLIENT;
    /** bvg, abc, rheinbahn, db, omp, coop */
  }

  hasRM(): boolean {
    if (this.getRMCustomers() === RM_CLIENT) {
      return true;
    }

    return false;
  }

  getOMCustomers(): string {
    const nonOMClients = [
      Customer.AIRBUSDS,
      Customer.HERRMANNULTRASCHALL,
      Customer.IAV,
      Customer.MAQUETGMBH,
      Customer.SEW,
      Customer.ATLAS,
      Customer.BELIMO,
      Customer.TAE,
      Customer.STADLER,
      Customer.EMZ,
      Customer.RADWELL,
      Customer.PLASTICOMNIUM,
      Customer.HAGENUK,
      Customer.THALES
    ];

    if (nonOMClients.includes(this.app.customers.expectCurrent)) {
      return NON_OM_CLIENT;
    }

    return OM_CLIENT;
  }

  // using RM module but with "specialities"
  getSpecialCustomers() {
    return USUAL_CONFIGURATION;
  }

  setData(customer: CustomerName, settings: Settings) {
    const buffer = touch(
      () => this.app.state.buffer,
      (arg) => {
        arg[customer] = touch(
          () => arg[customer],
          (arg1) => {
            arg1.settings = settings;
            if (arg1.docs == null) {
              arg1.docs = {};
            }
          }
        );
      }
    );

    this.app.state.next({ buffer });
  }

  async update(customer = this.current, isPcn?: boolean) {
    if (customer == null) {
      return;
    }
    if (this.app.RM.mode === "tree") {
      this.app.tree.currentColumns = [];
    }
    this.app.state.next({ customer });
    let settings: Settings;
    if (isPcn) {
      settings = await getCustomerSettingsWithoutUser(customer);
    } else {
      settings = await getCustomerSettings(customer);
    }
    this.app.customers.setData(customer, settings);
    this.dataProvider = settings.RM.list.dataProvider[0];
    this.app.proxy.settings = settings;
    if (this.app.RM.mode === "tree") {
      this.app.tree.currentColumns = this.app.tree.getTreeColumns();
    }
  }

  hasPortal() {
    // user has acces to portal
    const hasUserPortalAccess = this.app.customers.customers.includes(
      Customer.OMP
    );
    let hasClientPortalAccess = false;
    if (this.app.customers.getCustomerType() === TRANSPORT_CLIENT) {
      hasClientPortalAccess = true;
    }
    if (hasUserPortalAccess && hasClientPortalAccess) {
      return true;
    }
    return false;
  }
}
